import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Briefcase,
  GraduationCap,
  BadgeCheck,
  Trophy,
  Layers,
  FileText,
  User,
  Mail,
  Settings,
  LogOut,
  ExternalLink,
  Menu,
  X,
  Inbox,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/hero", label: "Hero Section", icon: User },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/experience", label: "Experience", icon: Briefcase },
  { to: "/admin/skills", label: "Skills", icon: Layers },
  { to: "/admin/education", label: "Education", icon: GraduationCap },
  { to: "/admin/certifications", label: "Certifications", icon: BadgeCheck },
  { to: "/admin/achievements", label: "Achievements", icon: Trophy },
  { to: "/admin/resume", label: "Resume", icon: FileText },
  { to: "/admin/contact", label: "Contact Info", icon: Mail },
  { to: "/admin/messages", label: "Inbox", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: loc.pathname }} replace />;
  }

  return (
    <div className="min-h-screen flex relative">
      {/* Sidebar — desktop fixed, mobile drawer */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 glass border-r border-white/10 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-5 flex items-center gap-4 border-b border-white/5">
          {/* <div className="w-24 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 grid place-items-center text-black font-display font-bold text-sm">B</div> */}
          <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-cyan-400 via-sky-500 to-violet-500 shadow-xl shadow-cyan-500/20 ring-1 ring-white/10 flex items-center justify-center">
          <span className="text-black text-[40px] font-black leading-none">
            B
          </span>
        </div>
          <div>
            <div className="font-display text-white text-sm">Portfolio CMS</div>
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-500">{user.email}</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-180px)]">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive
                    ? "bg-gradient-to-r from-cyan-400/15 to-violet-500/10 text-white border border-cyan-400/20"
                    : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                }`
              }
            >
              <l.icon size={16} />
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-white/5 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/[0.04] transition-colors"
            data-testid="admin-view-site"
          >
            <ExternalLink size={15} /> View Site
          </Link>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-300 hover:text-red-300 hover:bg-white/[0.04] transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* Backdrop (mobile) */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Content */}
      <main className="flex-1 min-w-0 lg:ml-0">
        <header className="sticky top-0 z-20 glass border-b border-white/5 px-4 lg:px-8 h-14 flex items-center justify-between">
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/5"
            aria-label="Toggle sidebar"
            data-testid="admin-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Admin</div>
          <div className="w-8" />
        </header>
        <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
